import { useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import {
  ArrowUpRight, Sun, Zap, Building2, Home, FileText, Search, HandCoins, Wrench,
  Sparkles, MessageCircle, Mail, MapPin, Menu, X, Phone, Shield, Award, Users, Star,
  TrendingDown, Check, Clock,
} from "lucide-react";

import BillAnalyzer from "./components/BillAnalyzer";
import ContactForm from "./components/ContactForm";
import CountUp from "./components/CountUp";

const NAV = [
  { href: "#nosotros", label: "Nosotros" },
  { href: "#servicios", label: "Servicios" },
  { href: "#proceso", label: "Proceso" },
  { href: "#solar", label: "Solar" },
  { href: "#contacto", label: "Contacto" },
];

/* ----- Real Unsplash photo URLs (curated) ----- */
const IMG = {
  hero: "https://images.unsplash.com/photo-1466611653911-95081537e5b7?auto=format&fit=crop&w=1400&q=80", // solar panels close up under blue sky
  solarWorker: "https://images.unsplash.com/photo-1509391366360-2e959784a276?auto=format&fit=crop&w=900&q=80", // solar panels closeup no people
  solarHouse: "https://images.unsplash.com/photo-1509391366360-2e959784a276?auto=format&fit=crop&w=1400&q=85",
  solarPanelSun: "https://images.unsplash.com/photo-1521618755572-156ae0cdd74d?auto=format&fit=crop&w=900&q=80", // solar panels close-up bright
  family: "https://images.unsplash.com/photo-1609220136736-443140cffec6?auto=format&fit=crop&w=900&q=80", // family at home cozy
  familyHome: "https://static.prod-images.emergentagent.com/jobs/7f0dcb16-a616-4eee-972a-ae3836485d46/images/f5a94059bbaf43ab0ff7f3ab11a5bc1ce505f9f1684c4642c486a4aca93d1f9f.jpeg", // familia en casa salon mediterraneo
  team: "https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=1200&q=80", // business meeting
  business: "https://static.prod-images.emergentagent.com/jobs/7f0dcb16-a616-4eee-972a-ae3836485d46/images/da7f5fc210fd19e78b9c1859c5705e7fe990a23d0bca6c9f836053873433db0e.jpeg", // split empresa + comunidad v2
  bill: "https://static.prod-images.emergentagent.com/jobs/7f0dcb16-a616-4eee-972a-ae3836485d46/images/1cc11621f41b21464ee03b4fcb47e56d9dce74e7337d2ea4d1bcee9e160d7cb8.jpeg", // revisando factura en casa
  alicante: "https://images.unsplash.com/photo-1680537732160-01750bae5217?auto=format&fit=crop&w=1400&q=80",
  rooftopSolar: "/placas-instalacion.webp", // foto real instalacion cliente
  energy: "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?auto=format&fit=crop&w=900&q=80",
};

const AVATAR = {
  marta: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=200&h=200&q=80",
  javier: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=200&h=200&q=80",
  pedro: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=200&h=200&q=80",
  founder: "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=400&q=80",
};

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

export default function Landing() {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden" data-testid="landing-root">
      <Header open={open} setOpen={setOpen} />
      <Hero />
      <TrustStrip />
      <About />
      <Solar />
      <Services />
      <MagiaSection />
      <Process />
      <Stats />
      <Projects />
      <Testimonials />
      <Contact />
      <Footer />
    </div>
  );
}

/* =========================
   TICKER (top, on dark)
========================= */
function Ticker() {
  const items = ["+25 AÑOS", "ESTUDIO GRATUITO", "SIN PERMANENCIAS", "AHORRO MEDIO 22%", "+1.000 CLIENTES", "SERVICIO HUMANO", "ALICANTE Y TODA ESPAÑA"];
  return (
    <div className="bg-ink text-white border-b border-ink overflow-hidden" data-testid="ticker">
      <div className="flex animate-ticker whitespace-nowrap py-2">
        {[...items, ...items, ...items].map((t, i) => (
          <span key={i} className="inline-flex items-center gap-2 px-6 text-[11px] font-bold tracking-[0.2em]">
            <Sun className="w-3 h-3 text-sun" /> {t}
            <span className="text-sun ml-2">●</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* =========================
   HEADER
========================= */
function Header({ open, setOpen }) {
  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-white/85 border-b border-ink/10" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-20 flex items-center justify-between">
        <a href="#" className="flex items-center" data-testid="brand-logo">
          <img src="/logo-amql.jpg" alt="AlgoMásQueLuz · tu gestor energético y solar" className="h-14 md:h-16 w-auto object-contain" />
        </a>
        <nav className="hidden md:flex items-center gap-7">
          {NAV.map((n) => (
            <a key={n.href} href={n.href}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className="text-sm font-medium text-ink/70 hover:text-ink transition-colors">
              {n.label}
            </a>
          ))}
          <a href="tel:+34966662327" className="hidden lg:inline-flex items-center gap-1 text-sm font-bold text-ink">
            <Phone className="w-4 h-4" /> 966 662 327
          </a>
          <a href="#contacto" data-testid="cta-header"
            className="inline-flex items-center gap-1 h-10 px-4 rounded-full bg-sun text-ink text-sm font-bold border-2 border-ink hover:bg-ink hover:text-sun transition-colors">
            Estudio gratuito <ArrowUpRight className="w-4 h-4" />
          </a>
        </nav>
        <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menú">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-ink/10 bg-white" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-3">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} onClick={() => setOpen(false)}
                className="py-1 text-ink font-medium"
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}>
                {n.label}
              </a>
            ))}
            <a href="tel:+34966662327" className="py-1 text-ink font-medium" data-testid="mobile-phone">
              📞 966 662 327
            </a>
            <a href="#contacto" onClick={() => setOpen(false)}
              className="mt-2 inline-flex items-center justify-center h-11 rounded-full bg-sun text-ink text-sm font-bold border-2 border-ink"
              data-testid="mobile-cta">
              Estudio gratuito
            </a>
          </div>
        </div>
      )}
    </header>
  );
}

/* =========================
   HERO — value-first, persuasive
========================= */
function Hero() {
  const valueProps = [
    { icon: <TrendingDown className="w-4 h-4" />, label: "Ahorro medio del 35% — hasta un 80% en algunos casos" },
    { icon: <Wrench className="w-4 h-4" />, label: "Cambiamos de tarifa por ti — papeleo cero" },
    { icon: <Sun className="w-4 h-4" />, label: "Instalamos placas solares llave en mano" },
    { icon: <Users className="w-4 h-4" />, label: "Particulares · Pymes · Comunidades de propietarios" },
  ];
  return (
    <section className="relative hero-spotlight overflow-hidden" data-testid="hero">
      <div className="absolute inset-0 dot-grid opacity-50 [mask-image:radial-gradient(ellipse_at_top,black,transparent_70%)]" />
      <div className="relative max-w-7xl mx-auto px-5 md:px-8 pt-12 md:pt-20 pb-16 md:pb-24">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* LEFT: copy */}
          <div className="lg:col-span-7">
            <motion.h1
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="font-serif text-[clamp(2.6rem,6.5vw,5.2rem)] leading-[0.92] tracking-tighter text-ink text-balance"
              data-testid="hero-headline"
            >
              Bajamos tu factura <br className="hidden md:block" />
              de la luz<span className="text-sun">.</span> <br />
              <span className="italic font-normal">Tú no haces </span><span className="tag-line-thick">nada.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-6 text-lg md:text-xl text-ink/70 max-w-xl leading-relaxed"
            >
              Llevamos 25 años haciendo una sola cosa: que pagues menos por la luz. Optimizamos tarifas, <strong className="text-ink">eliminamos servicios que no usas y que te cobran mes a mes</strong>, y además instalamos placas solares — para particulares, pymes y comunidades de propietarios.
            </motion.p>

            {/* VALUE PILLARS */}
            <motion.ul
              initial="hidden" animate="show"
              variants={{
                hidden: {}, show: { transition: { staggerChildren: 0.08, delayChildren: 0.3 } },
              }}
              className="mt-7 space-y-2.5 max-w-xl"
              data-testid="hero-value-list"
            >
              {valueProps.map((v, i) => (
                <motion.li
                  key={i}
                  variants={{ hidden: { opacity: 0, x: -10 }, show: { opacity: 1, x: 0 } }}
                  className="flex items-center gap-3"
                  data-testid={`hero-value-${i}`}
                >
                  <span className="w-7 h-7 rounded-full bg-sun text-ink flex items-center justify-center shrink-0 border border-ink">
                    <Check className="w-4 h-4" strokeWidth={2.5} />
                  </span>
                  <span className="text-ink font-medium">{v.label}</span>
                </motion.li>
              ))}
            </motion.ul>

            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.55 }}
              className="mt-8 flex flex-wrap items-center gap-3"
            >
              <a href="#contacto" data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-sun text-ink font-bold border-2 border-ink hover:bg-ink hover:text-sun transition-colors">
                Quiero mi estudio gratuito <ArrowUpRight className="w-4 h-4" />
              </a>
              <a href="#calcula" data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-white text-ink font-bold border-2 border-ink hover:bg-paper transition-colors">
                <FileText className="w-4 h-4" /> Enviar mi factura
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="mt-8 flex flex-wrap items-center gap-5 text-sm"
            >
              <span className="inline-flex items-center gap-1.5 text-ink/70">
                <Star className="w-4 h-4 fill-sun text-sun" /> <strong className="text-ink">4,9</strong> · +180 reseñas
              </span>
              <span className="inline-flex items-center gap-1.5 text-ink/70">
                <Shield className="w-4 h-4 text-ink" /> Sin permanencias
              </span>
              <span className="inline-flex items-center gap-1.5 text-ink/70">
                <Clock className="w-4 h-4 text-ink" /> Respuesta en 24h
              </span>
            </motion.div>
          </div>

          {/* RIGHT: hero visual collage */}
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 relative"
            data-testid="hero-visual"
          >
            {/* Yellow back card */}
            <div className="absolute inset-0 translate-x-4 translate-y-4 bg-sun rounded-3xl border-2 border-ink" />

            {/* Main image card */}
            <div className="relative rounded-3xl overflow-hidden border-2 border-ink bg-white">
              <img
                src={IMG.family}
                alt="Familia tranquila en casa"
                className="w-full h-[420px] md:h-[540px] object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent" />

              {/* Bottom caption inside image */}
              <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                <p className="font-serif text-2xl tracking-tight leading-tight">
                  "Ahorramos <span className="text-sun">980€</span> al año.<br />Sin movernos del sofá."
                </p>
                <p className="text-white/80 text-sm mt-2">Familia García · Elche</p>
              </div>
            </div>

            {/* Floating savings sticker top */}
            <motion.div
              initial={{ opacity: 0, y: -10, rotate: -8 }}
              animate={{ opacity: 1, y: 0, rotate: -8 }}
              transition={{ delay: 0.6, type: "spring", stiffness: 200 }}
              className="absolute -top-3 -left-2 md:-top-4 md:-left-4 bg-ink text-white rounded-2xl border-2 border-ink shadow-[5px_5px_0_0_#FFD600] px-3 py-2.5 md:px-4 md:py-3 z-10"
              data-testid="hero-sticker-savings"
            >
              <div className="text-[9px] md:text-[10px] uppercase tracking-widest text-sun font-bold">Ahorro medio</div>
              <div className="font-serif text-2xl md:text-3xl leading-none mt-0.5">
                <CountUp to={74} prefix="" suffix=" €" /><span className="text-sun text-sm md:text-base">/mes</span>
              </div>
            </motion.div>

            {/* Floating mini stat bottom-right */}
            <motion.div
              initial={{ opacity: 0, y: 10, rotate: 6 }}
              animate={{ opacity: 1, y: 0, rotate: 6 }}
              transition={{ delay: 0.75, type: "spring", stiffness: 200 }}
              className="absolute -bottom-3 -right-2 md:-bottom-5 md:-right-3 bg-sun text-ink rounded-2xl border-2 border-ink px-3 py-2.5 md:px-4 md:py-3 z-10"
              data-testid="hero-sticker-clients"
            >
              <div className="flex items-center gap-2">
                <Users className="w-3.5 h-3.5 md:w-4 md:h-4" />
                <div>
                  <div className="font-bold text-sm md:text-base leading-none">+1.000 clientes</div>
                  <div className="text-[9px] md:text-[10px] uppercase tracking-widest font-bold opacity-70 mt-0.5">activos hoy</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* =========================
   MAGIA SECTION — envíanos tu factura, lo hacemos nosotros
========================= */
function MagiaSection() {
  const features = [
    { icon: <FileText className="w-5 h-5" />, title: "Nos la envías", text: "Sube una foto o PDF de tu factura. Es todo lo que necesitamos." },
    { icon: <Search className="w-5 h-5" />, title: "La estudiamos nosotros", text: "Un gestor humano analiza tu factura — no un robot." },
    { icon: <HandCoins className="w-5 h-5" />, title: "Recibes el ahorro", text: "En 24h te enviamos cuánto vas a ahorrar y los pasos para el cambio." },
  ];
  return (
    <section id="calcula" className="relative bg-ink text-white overflow-hidden" data-testid="magia">
      <div className="absolute inset-0 grain-overlay opacity-[0.08] mix-blend-screen pointer-events-none" />
      <div className="absolute -top-32 -left-32 w-[28rem] h-[28rem] bg-sun/10 rounded-full blur-3xl" />
      <div className="absolute -bottom-32 -right-32 w-[24rem] h-[24rem] bg-sun/10 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* LEFT: explainer */}
          <motion.div {...fadeUp} className="lg:col-span-6">
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-sun font-bold">
              <FileText className="w-3.5 h-3.5" /> Envíanos tu factura · estudio gratuito
            </span>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tighter mt-4 leading-tight pb-3 text-balance">
              Déjanos tu factura. <br />
              <span className="italic">Hacemos el resto</span> <span className="text-sun">por ti.</span>
            </h2>
            <p className="text-white/75 mt-6 text-lg leading-relaxed max-w-md">
              Un gestor energético revisa tu factura a mano y te envía un informe claro con el ahorro real. Después, si decides proceder, nosotros nos ocupamos del cambio de tarifa y los trámites.
            </p>

            <div className="mt-8 space-y-4">
              {features.map((f, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="flex items-start gap-4"
                  data-testid={`magia-feature-${i}`}
                >
                  <div className="w-11 h-11 rounded-full bg-sun text-ink flex items-center justify-center shrink-0 border-2 border-sun font-bold">
                    {i + 1}
                  </div>
                  <div>
                    <div className="font-serif text-xl text-white">{f.title}</div>
                    <div className="text-white/65 text-sm mt-0.5">{f.text}</div>
                  </div>
                </motion.div>
              ))}
            </div>

            <p className="mt-8 text-xs text-white/50 max-w-sm">
              * Sin compromiso. Si tras el estudio no podemos ahorrarte, te lo decimos. Sin coste.
            </p>
          </motion.div>

          {/* RIGHT: the form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-6 relative flex justify-center"
          >
            <div className="relative">
              <BillAnalyzer />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* =========================
   TRUST STRIP — yellow band
========================= */
function TrustStrip() {
  const items = [
    { num: "+25", label: "años de experiencia" },
    { num: "+1.000", label: "clientes satisfechos" },
    { num: "35%", label: "ahorro medio" },
    { num: "80%", label: "ahorro máximo" },
    { num: "24h", label: "respuesta garantizada" },
  ];
  return (
    <section className="bg-sun border-y-2 border-ink" data-testid="trust-strip">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-8">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
          {items.map((it, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.06 }}
              className="flex flex-col items-center md:items-start"
              data-testid={`trust-${i}`}
            >
              <div className="font-serif text-3xl md:text-4xl font-semibold text-ink tracking-tight leading-none">
                {it.num}
              </div>
              <div className="mt-1 text-xs uppercase tracking-[0.15em] text-ink/70 font-bold">{it.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =========================
   ABOUT — confianza, persuasiva
========================= */
function About() {
  return (
    <section id="nosotros" className="relative" data-testid="about">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <motion.div {...fadeUp} className="lg:col-span-5 relative">
            <div className="relative rounded-3xl overflow-hidden border-2 border-ink shadow-[10px_10px_0_0_#FFD600]">
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=1000&q=80"
                alt="Gestor energético atendiendo a un cliente"
                className="w-full h-[500px] object-cover"
                data-testid="about-image"
              />
              <div className="absolute inset-x-0 bottom-0 p-5 md:pr-56 bg-gradient-to-t from-ink/85 to-transparent text-white">
                <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-sun">El equipo</p>
                <p className="font-serif text-xl mt-1 leading-tight">Si no conseguimos ahorro en tu factura, te lo decimos.</p>
              </div>
            </div>
            <div className="absolute -bottom-6 -right-4 bg-sun text-ink rounded-2xl p-5 max-w-[240px] border-2 border-ink shadow-[6px_6px_0_0_#0F0F0F] hidden md:block">
              <div className="font-serif text-4xl text-ink leading-none">
                25<span className="text-ink/60 text-2xl"> años</span>
              </div>
              <div className="text-xs uppercase tracking-widest font-bold mt-2">
                bajando facturas a familias y empresas
              </div>
            </div>
          </motion.div>

          <motion.div {...fadeUp} className="lg:col-span-7">
            <Eyebrow>Por qué confiar en nosotros</Eyebrow>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95] mt-3 text-balance">
              No vendemos energía. <br className="hidden md:block" />
              <span className="italic">Bajamos</span> <span className="tag-line">tu factura.</span>
            </h2>
            <p className="mt-6 text-lg text-ink/75 leading-relaxed max-w-xl">
              Somos los gestores energéticos que tu vecino lleva 25 años recomendando. Tú nos envías la factura y nosotros — <strong className="text-ink">no un robot, no un call-center</strong> — la analizamos persona a persona. Te decimos cuánto vas a ahorrar y nos ocupamos del cambio de tarifa, los trámites con la comercializadora y la instalación solar si te conviene.
            </p>
            <p className="mt-4 text-lg text-ink/75 leading-relaxed max-w-xl">
              <strong className="text-ink">El estudio es siempre gratuito.</strong> Si tras revisar tu factura no podemos ahorrarte dinero, te lo decimos al momento y sin enredos. Solo cobramos cuando hay ahorro real para ti.
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Pillar icon={<Shield className="w-5 h-5" />} title="Independientes">
                No trabajamos para ninguna comercializadora. Trabajamos para ti.
              </Pillar>
              <Pillar icon={<Users className="w-5 h-5" />} title="Cercanos">
                Hablas con la misma persona de principio a fin.
              </Pillar>
              <Pillar icon={<Award className="w-5 h-5" />} title="Transparentes">
                Si no podemos bajarte la factura, te lo decimos claro. Sin enredos.
              </Pillar>
            </div>
            <a href="#contacto" className="mt-8 inline-flex items-center gap-2 font-bold underline underline-offset-4 hover:text-sunDeep" data-testid="about-cta">
              Hablar con un gestor real <ArrowUpRight className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Pillar({ icon, title, children }) {
  return (
    <div className="bg-paper rounded-2xl p-5 border border-ink/10 hover:border-ink hover:bg-cream transition-all">
      <div className="w-9 h-9 rounded-full bg-sun text-ink flex items-center justify-center">{icon}</div>
      <h4 className="font-serif text-lg mt-3">{title}</h4>
      <p className="text-sm text-ink/70 mt-1.5 leading-relaxed">{children}</p>
    </div>
  );
}

function Eyebrow({ children }) {
  return (
    <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-ink/60 font-bold">
      <span className="w-6 h-px bg-ink" />{children}
    </span>
  );
}

/* =========================
   SERVICES — Cards with detail dialog
========================= */
const SERVICE_DETAILS = {
  audit: {
    kicker: "Auditoría",
    icon: <Zap className="w-5 h-5" />,
    title: "Optimización de factura",
    intro: "Te enseñamos línea por línea dónde pagas de más en tu factura — y lo arreglamos.",
    bullets: [
      "Auditamos potencia contratada, tarifa, complementos y picos de consumo",
      "Comparamos con más de 30 comercializadoras del mercado libre",
      "Negociamos por ti sin llamadas pesadas",
      "Gestionamos el cambio completo: alta, baja, lecturas",
      "Reporting trimestral del ahorro real conseguido",
    ],
    cta: "Quiero auditar mi factura",
  },
  solar: {
    kicker: "Autoconsumo",
    icon: <Sun className="w-5 h-5" />,
    title: "Placas solares",
    intro: "Solar dimensionado a tu consumo real, instalación llave en mano y excedentes compensados.",
    bullets: [
      "Estudio de viabilidad y orientación del tejado — gratuito",
      "Dimensionado realista (no te sobreinstalamos para inflar el presupuesto)",
      "Instalación llave en mano: permisos, montaje y legalización",
      "Tramitamos subvenciones y bonificaciones del IBI",
      "Mantenimiento y monitorización durante años",
    ],
    cta: "Quiero estudio solar",
  },
  business: {
    kicker: "Pymes & Comunidades",
    icon: <Building2 className="w-5 h-5" />,
    title: "Empresas y comunidades de propietarios",
    intro: "Asesoría energética continua para negocios y comunidades. Negociación colectiva y reporting mensual.",
    bullets: [
      "Auditoría energética completa con análisis de consumos por horarios",
      "Negociación colectiva multi-punto (varios suministros, mismo gestor)",
      "Asesoría continua y reuniones de seguimiento",
      "Reporting mensual con KPIs claros para junta directiva",
      "Coordinación directa con administradores de fincas",
    ],
    cta: "Quiero estudio para mi negocio",
  },
  home: {
    kicker: "Hogares",
    icon: <Home className="w-5 h-5" />,
    title: "Tu casa",
    intro: "Trato cercano, cero tecnicismos. Te explicamos tu factura y te bajamos el recibo del mes.",
    bullets: [
      "Análisis personalizado de tu factura familiar",
      "Ajuste de potencia óptima (la mayoría tiene contratado de más)",
      "Cambio de tarifa sin papeleo por tu parte",
      "Recomendación de placas solares si te conviene económicamente",
      "Tu gestor personal: hablas con la misma persona siempre",
    ],
    cta: "Quiero bajar mi factura",
  },
};

function Services() {
  const [openId, setOpenId] = useState(null);
  const cards = [
    { id: "audit", img: IMG.bill, big: true },
    { id: "solar", img: IMG.rooftopSolar },
    { id: "business", img: IMG.business },
    { id: "home", img: IMG.familyHome },
  ];
  return (
    <section id="servicios" className="bg-paper border-y border-ink/10" data-testid="services">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
        <motion.div {...fadeUp} className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <Eyebrow>Qué hacemos</Eyebrow>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tighter mt-3 leading-[0.95] text-balance">
              Cuatro maneras de <span className="tag-line">bajar tu factura</span>. Una sola conversación.
            </h2>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {cards.map((c, i) => {
            const d = SERVICE_DETAILS[c.id];
            return (
              <ServiceCard
                key={c.id}
                index={i}
                kicker={d.kicker} icon={d.icon} title={d.title} text={d.intro}
                img={c.img} big={c.big}
                onOpen={() => setOpenId(c.id)}
                testid={`service-${c.id}`}
              />
            );
          })}
        </div>
      </div>

      <ServiceDialog open={!!openId} detail={openId ? SERVICE_DETAILS[openId] : null} onClose={() => setOpenId(null)} />
    </section>
  );
}

function ServiceCard({ index, kicker, icon, title, text, img, onOpen, testid }) {
  return (
    <motion.button
      type="button"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: index * 0.08 }}
      onClick={onOpen}
      data-testid={`${testid}-button`}
      className="group relative text-left bg-white rounded-3xl border-2 border-ink overflow-hidden hover:-translate-y-1 transition-transform hover:shadow-[10px_10px_0_0_#FFD600] focus:outline-none focus-visible:ring-4 focus-visible:ring-sun"
    >
      <div className="relative overflow-hidden h-56">
        <img src={img} alt={title} className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/40 to-transparent" />
        <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-sun text-ink text-[11px] uppercase tracking-widest font-bold border border-ink">
          {icon} {kicker}
        </span>
      </div>
      <div className="p-6">
        <h3 className="font-serif text-2xl md:text-3xl tracking-tight">{title}</h3>
        <p className="text-ink/70 mt-2 leading-relaxed">{text}</p>
        <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-ink group-hover:text-sunDeep">
          Más información <ArrowUpRight className="w-4 h-4" />
        </span>
      </div>
    </motion.button>
  );
}

function ServiceDialog({ open, detail, onClose }) {
  if (!detail) return null;
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-[100] bg-ink/70 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
          data-testid="service-dialog"
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", damping: 26, stiffness: 240 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full md:max-w-2xl bg-white rounded-t-3xl md:rounded-3xl border-2 border-ink overflow-hidden shadow-[10px_10px_0_0_#FFD600]"
          >
            <button
              onClick={onClose}
              data-testid="service-dialog-close"
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-ink text-sun flex items-center justify-center hover:bg-sun hover:text-ink border-2 border-ink z-10"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="bg-sun border-b-2 border-ink px-6 md:px-8 py-5">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-ink text-sun text-[10px] uppercase tracking-widest font-bold">
                {detail.icon} {detail.kicker}
              </span>
              <h3 className="font-serif text-3xl md:text-4xl tracking-tight mt-3 text-ink leading-tight">
                {detail.title}
              </h3>
            </div>
            <div className="p-6 md:p-8 max-h-[60vh] overflow-y-auto">
              <p className="text-ink/75 leading-relaxed text-lg">{detail.intro}</p>
              <ul className="mt-6 space-y-3" data-testid="service-dialog-bullets">
                {detail.bullets.map((b, i) => (
                  <li key={i} className="flex gap-3 items-start">
                    <span className="w-6 h-6 rounded-full bg-sun text-ink flex items-center justify-center shrink-0 border border-ink mt-0.5">
                      <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
                    </span>
                    <span className="text-ink/85 leading-relaxed">{b}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-7 flex flex-wrap gap-3">
                <a
                  href="#contacto" onClick={onClose}
                  data-testid="service-dialog-cta"
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-ink text-sun font-bold border-2 border-ink hover:bg-sun hover:text-ink transition-colors"
                >
                  {detail.cta} <ArrowUpRight className="w-4 h-4" />
                </a>
                <a
                  href="https://wa.me/34624476295" target="_blank" rel="noreferrer"
                  data-testid="service-dialog-whatsapp"
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-white text-ink font-bold border-2 border-ink hover:bg-paper transition-colors"
                >
                  <MessageCircle className="w-4 h-4" /> Hablar por WhatsApp
                </a>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* =========================
   PROCESS
========================= */
function Process() {
  const steps = [
    { icon: <FileText className="w-5 h-5" />, title: "Nos envías la factura", text: "WhatsApp, email o subida en la web. En 1 minuto." },
    { icon: <Search className="w-5 h-5" />, title: "Estudiamos qué pagas", text: "Cruzamos datos con todo el mercado y detectamos sobrecostes." },
    { icon: <HandCoins className="w-5 h-5" />, title: "Te proponemos ahorro", text: "Una propuesta clara, en euros, sin tecnicismos." },
    { icon: <Wrench className="w-5 h-5" />, title: "Gestionamos el cambio", text: "Tú no haces nada. Nosotros nos ocupamos de todo." },
  ];
  return (
    <section id="proceso" className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28" data-testid="process">
      <motion.div {...fadeUp} className="mb-14 max-w-2xl">
        <Eyebrow>Proceso</Eyebrow>
        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tighter mt-3 leading-[0.95] text-balance">
          Cuatro pasos. <span className="italic"><span className="tag-line">Cero esfuerzo</span></span> por tu parte.
        </h2>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
        {steps.map((s, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: i * 0.1 }}
            className="relative"
            data-testid={`process-step-${i + 1}`}
          >
            <div className="relative bg-white rounded-2xl border-2 border-ink p-6 hover:bg-sun transition-colors group">
              <div className="flex items-center justify-between mb-5">
                <div className="w-11 h-11 rounded-full bg-ink text-sun flex items-center justify-center group-hover:bg-white group-hover:text-ink transition-colors">
                  {s.icon}
                </div>
                <span className="font-serif text-5xl text-ink/15 group-hover:text-ink/40 leading-none">{String(i + 1).padStart(2, "0")}</span>
              </div>
              <h3 className="font-serif text-xl md:text-2xl tracking-tight">{s.title}</h3>
              <p className="text-ink/70 mt-2 text-sm leading-relaxed group-hover:text-ink/80">{s.text}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* =========================
   STATS
========================= */
function Stats() {
  const items = [
    { value: 25, suffix: "+", label: "Años de oficio" },
    { value: 1000, prefix: "+", label: "Clientes activos" },
    { value: 35, suffix: "%", label: "Ahorro medio" },
    { value: 80, suffix: "%", label: "Ahorro máximo" },
  ];
  return (
    <section className="bg-ink text-white relative overflow-hidden" data-testid="stats">
      <div className="absolute inset-0 grain-overlay opacity-[0.08] mix-blend-screen pointer-events-none" />
      <div className="absolute -top-20 -right-20 w-80 h-80 bg-sun/10 rounded-full blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
        <motion.div {...fadeUp} className="mb-12 max-w-2xl">
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-sun font-bold">
            <span className="w-6 h-px bg-sun" /> Cifras
          </span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tighter mt-3 leading-[0.95] text-balance">
            La energía no debería ser un <span className="text-sun italic">quebradero de cabeza.</span>
          </h2>
        </motion.div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {items.map((it, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.7, delay: i * 0.08 }}
              className="border-t-2 border-sun pt-5"
              data-testid={`stat-${i}`}
            >
              <div className="font-serif text-[clamp(3rem,6vw,5rem)] leading-none tracking-tighter text-white">
                <CountUp to={it.value} prefix={it.prefix || ""} suffix={it.suffix || ""} decimals={it.decimals || 0} duration={1.6} />
              </div>
              <div className="mt-3 text-[11px] uppercase tracking-[0.22em] text-white/60 font-bold">{it.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =========================
   SOLAR
========================= */
function Solar() {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], [0, -150]);
  return (
    <section id="solar" className="relative bg-paper border-y border-ink/10" data-testid="solar">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
        <motion.div {...fadeUp} className="max-w-2xl mb-12">
          <Eyebrow>Autoconsumo</Eyebrow>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tighter mt-3 leading-[0.95] text-balance">
            <span className="tag-line">Placas solares</span> pensadas para ti.
          </h2>
          <p className="mt-5 text-lg text-ink/70 leading-relaxed max-w-xl">
            Instalación llave en mano en Elche y la provincia de Alicante. Dimensionado real según tu consumo, tramitación de subvenciones y compensación de excedentes desde el primer mes.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-8 items-stretch">
          <motion.div
            initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.8 }}
            className="lg:col-span-7 relative rounded-3xl overflow-hidden min-h-[500px] border-2 border-ink"
          >
            <motion.img
              src={IMG.solarHouse}
              alt="Casa con placas solares"
              className="absolute inset-0 w-full h-full object-cover scale-110"
              style={{ y }}
              data-testid="solar-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/95 via-ink/60 to-ink/10" />
            <div className="relative h-full flex flex-col justify-end p-7 md:p-10 lg:pr-52 text-white">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sun text-ink text-[11px] uppercase tracking-widest font-bold mb-4 w-fit border border-ink">
                <Sun className="w-3.5 h-3.5" /> Autoconsumo solar
              </span>
              <h3 className="font-serif text-3xl md:text-5xl tracking-tighter leading-[1.0]">
                Tu tejado puede pagar <span className="italic text-sun">la mitad</span> de tu factura.
              </h3>
              <p className="text-white/85 mt-4 max-w-md leading-relaxed">
                Dimensionado real, sin sobreinstalar. Te garantizamos un retorno claro y compensación de excedentes desde el primer mes.
              </p>
            </div>

            {/* Floating panel image */}
            <div className="hidden lg:block absolute top-6 right-6 w-40 h-40 rounded-2xl overflow-hidden border-2 border-sun shadow-2xl rotate-3">
              <img src={IMG.solarPanelSun} alt="Placas solares" className="w-full h-full object-cover" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-5 flex flex-col gap-4"
          >
            {[
              { title: "Estudio de viabilidad gratuito", text: "Estudio térmico y orientación del tejado. Sabes la rentabilidad antes de firmar." },
              { title: "Instalación llave en mano", text: "Permisos, instalación y legalización. Tú no haces papeleo." },
              { title: "Compensación de excedentes", text: "Lo que produces y no consumes, se descuenta de tu factura." },
              { title: "Mantenimiento y monitorización", text: "Seguimos tu instalación durante años. Sin sorpresas." },
            ].map((b, i) => (
              <div key={i} className="bg-white border-2 border-ink rounded-2xl p-5 md:p-6 hover:bg-sun transition-colors group" data-testid={`solar-benefit-${i}`}>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-ink text-sun flex items-center justify-center shrink-0 font-serif font-bold group-hover:bg-white group-hover:text-ink">
                    {i + 1}
                  </div>
                  <div>
                    <h3 className="font-serif text-xl tracking-tight">{b.title}</h3>
                    <p className="text-ink/70 text-sm mt-1.5 leading-relaxed group-hover:text-ink/80">{b.text}</p>
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* =========================
   PROJECTS gallery
========================= */
function Projects() {
  const projects = [
    { img: "https://images.unsplash.com/photo-1497440001374-f26997328c1b?auto=format&fit=crop&w=1200&q=80", tag: "Solar · 3,6 kWp", title: "Vivienda unifamiliar · Elche", saving: "487 €/año" },
    { img: "https://images.unsplash.com/photo-1552566626-52f8b828add9?auto=format&fit=crop&w=1200&q=80", tag: "Pyme · Hostelería", title: "Restaurante — Cadena el Águila", saving: "3.509 €/año" },
    { img: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=1200&q=80", tag: "Solar · Comunidad", title: "Comunidad de propietarios · Murcia", saving: "1.321 €/año" },
  ];
  return (
    <section className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28" data-testid="projects">
      <motion.div {...fadeUp} className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-6">
        <div className="max-w-2xl">
          <Eyebrow>Casos reales</Eyebrow>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tighter mt-3 leading-[0.95] text-balance">
            Lo que <span className="tag-line">ya hemos hecho</span> por otros.
          </h2>
        </div>
      </motion.div>
      <div className="grid md:grid-cols-3 gap-5">
        {projects.map((p, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.08 }}
            className="group relative rounded-3xl overflow-hidden border-2 border-ink"
            data-testid={`project-${i}`}
          >
            <div className="aspect-[4/5] overflow-hidden">
              <img src={p.img} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-ink/95 via-ink/30 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-6 text-white">
              <span className="inline-block px-2.5 py-1 rounded-full bg-sun text-ink text-[10px] uppercase tracking-widest font-bold mb-3">
                {p.tag}
              </span>
              <h3 className="font-serif text-2xl tracking-tight leading-tight">{p.title}</h3>
              <div className="mt-2 inline-flex items-center gap-2 text-sun font-bold">
                <HandCoins className="w-4 h-4" /> {p.saving}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* =========================
   TESTIMONIALS — with avatars
========================= */
function Testimonials() {
  const items = [
    {
      quote: "Pasamos de pagar 2.410€ a pagar 1.340€ al mes en el restaurante. Sin cambiar absolutamente nada del día a día.",
      author: "Marta Beltrán", role: "La Cantina · Alicante", avatar: AVATAR.marta,
    },
    {
      quote: "Pensaba que las placas eran imposibles para mi tejado. Me hicieron el estudio en 48h y se paga sola en 6 años.",
      author: "Javier Núñez", role: "Vivienda unifamiliar · Elche", avatar: AVATAR.javier,
    },
    {
      quote: "Hugo gestiona todo con la comercializadora por nosotros. Un alivio. Y los números cuadran cada mes.",
      author: "Pedro & Inés", role: "Comunidad de propietarios · Murcia", avatar: AVATAR.pedro,
    },
  ];
  return (
    <section className="bg-sun border-y-2 border-ink relative overflow-hidden" data-testid="testimonials">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
        <motion.div {...fadeUp} className="max-w-2xl mb-14">
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-ink font-bold">
            <span className="w-6 h-px bg-ink" /> Lo que dicen de nosotros
          </span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tighter mt-3 leading-[0.95] text-ink text-balance">
            Más de mil familias y negocios <span className="italic">duermen mejor.</span>
          </h2>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-6">
          {items.map((t, i) => (
            <motion.figure
              key={i}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.7, delay: i * 0.08 }}
              className={`bg-white border-2 border-ink rounded-3xl p-7 hover:-translate-y-1 transition-transform ${i === 1 ? "md:translate-y-6" : ""}`}
              data-testid={`testimonial-${i}`}
            >
              <div className="flex gap-1 text-ink mb-3">
                {[...Array(5)].map((_, k) => <Star key={k} className="w-4 h-4 fill-current" />)}
              </div>
              <blockquote className="font-serif text-xl md:text-2xl leading-snug tracking-tight text-balance text-ink">
                "{t.quote}"
              </blockquote>
              <figcaption className="mt-6 pt-4 border-t border-ink/10 flex items-center gap-3">
                <img src={t.avatar} alt={t.author} className="w-11 h-11 rounded-full object-cover border-2 border-ink" />
                <div>
                  <div className="font-bold text-ink">{t.author}</div>
                  <div className="text-sm text-ink/60">{t.role}</div>
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =========================
   CONTACT — dark
========================= */
function Contact() {
  return (
    <section id="contacto" className="relative bg-ink text-white" data-testid="contact">
      <div className="absolute inset-0 grain-overlay opacity-[0.07] mix-blend-screen pointer-events-none" />
      <div className="absolute -top-20 -left-20 w-96 h-96 bg-sun/10 rounded-full blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-12">
          <motion.div {...fadeUp} className="lg:col-span-6">
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-sun font-bold">
              <span className="w-6 h-px bg-sun" /> Hablemos
            </span>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tighter leading-[0.95] mt-4 text-balance">
              Diez minutos. <span className="italic text-sun">Una factura.</span> Una respuesta clara.
            </h2>
            <p className="text-white/70 mt-6 max-w-md text-lg leading-relaxed">
              Sin permanencias, sin compromisos. Recibirás un estudio personalizado en menos de 24h.
            </p>

            <div className="mt-10 space-y-4">
              <ContactRow
                href="https://wa.me/34624476295" target external
                icon={<MessageCircle className="w-5 h-5" />}
                label="WhatsApp" value="624 476 295" testid="contact-whatsapp"
              />
              <ContactRow
                href="mailto:estudios@algomasqueluz.com"
                icon={<Mail className="w-5 h-5" />}
                label="Email" value="estudios@algomasqueluz.com" testid="contact-email-link"
              />
              <ContactRow
                href="tel:+34966662327"
                icon={<Phone className="w-5 h-5" />}
                label="Teléfono" value="966 662 327" testid="contact-phone"
              />
              <ContactRow
                icon={<MapPin className="w-5 h-5" />}
                label="Oficina" value="C/ Doctor Caro 87, Bajo · Elche, Alicante" testid="contact-location"
              />
            </div>
          </motion.div>

          <motion.div {...fadeUp} transition={{ duration: 0.7, delay: 0.1 }} className="lg:col-span-6 lg:pl-8 lg:border-l border-white/10">
            <div className="bg-white/[0.03] border border-white/10 rounded-3xl p-6 md:p-8 backdrop-blur-sm">
              <ContactForm />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function ContactRow({ href, icon, label, value, target, external, testid }) {
  const Wrap = href ? "a" : "div";
  const props = href ? { href, target: target ? "_blank" : undefined, rel: external ? "noreferrer" : undefined } : {};
  return (
    <Wrap {...props} className="flex items-center gap-4 group" data-testid={testid}>
      <div className="w-11 h-11 rounded-full bg-white/5 border border-white/15 flex items-center justify-center group-hover:bg-sun group-hover:text-ink group-hover:border-sun transition-colors">
        {icon}
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-white/50 font-bold">{label}</div>
        <div className="font-serif text-xl">{value}</div>
      </div>
    </Wrap>
  );
}

/* =========================
   FOOTER
========================= */
function Footer() {
  return (
    <footer className="bg-ink text-white/70 border-t border-white/10" data-testid="footer">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="bg-white rounded-xl p-1.5">
            <img src="/logo-amql.jpg" alt="AlgoMásQueLuz" className="h-10 w-auto object-contain" />
          </div>
          <span className="text-white/40 text-sm">© {new Date().getFullYear()} AlgoMásQueLuz</span>
        </div>
        <p className="text-xs text-white/60 text-center md:text-right">
          C/ Doctor Caro 87, Bajo · Elche, Alicante · 966 662 327 · estudios@algomasqueluz.com
        </p>
      </div>
    </footer>
  );
}
